import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from 'react-jss';
import { useLazyQuery } from '@apollo/client';
import { Button, AlertService, Theme as theme, TextInput } from '@spoiler-alert/ui-library';
import { BulkImportRunByIdQuery } from '../../graphql/queries';

const useStyles = createUseStyles({
  container: {
    marginTop: 24,
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  inputRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  input: {
    width: 300,
  },
  detailsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    border: `1px solid ${theme.grey80}`,
    padding: 16,
    borderRadius: 4,
  },
  detailsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: 0,
  },
  errorSectionTitle: {
    marginTop: 0,
    marginBottom: 0,
    color: theme.errorColor,
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: '150px 1fr',
    gap: 8,
    fontSize: 14,
  },
  errorText: {
    color: theme.errorColor,
  },
  fileErrorsContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    background: theme.red5,
    padding: 12,
    borderRadius: 4,
    border: `1px solid ${theme.errorColor}`,
  },
  fileError: {
    paddingBottom: 8,
  },
  fileErrorWithBorder: {
    paddingBottom: 8,
    borderBottom: `1px solid ${theme.grey80}`,
  },
  errorDetail: {
    fontSize: 14,
  },
  rowErrorsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    borderRadius: 4,
    overflowY: 'auto',
  },
  rowError: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    padding: 12,
    border: `1px solid ${theme.redDark}`,
    background: theme.red5,
    borderRadius: 4,
  },
  rowData: {
    fontSize: 12,
    color: theme.grey60,
    fontFamily: 'monospace',
  },
  errorListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginLeft: 16,
  },
  errorItem: {
    fontSize: 14,
  },
  errorBullet: {
    color: theme.errorColor,
  },
  errorField: {
    color: theme.grey60,
  },
  noErrorsContainer: {
    padding: 16,
    textAlign: 'center',
    color: theme.successColor,
  },
  fieldLabel: {
    fontWeight: 'bold',
  },
});

const ViewImportErrorsWidget = () => {
  const classes = useStyles();
  const [bulkImportRunId, setBulkImportRunId] = useState('');
  const [fetchBulkImportRun, { loading, data }] = useLazyQuery(BulkImportRunByIdQuery, {
    fetchPolicy: 'network-only',
    onError: (err) => {
      AlertService.alert({ type: 'warning', message: `Error loading import run: ${err.message}` });
    },
  });

  const handleFetch = () => {
    if (!bulkImportRunId.trim()) {
      return AlertService.alert({ type: 'warning', message: 'Please enter a Bulk Import Run ID.' });
    }
    fetchBulkImportRun({ variables: { bulkImportRunId: bulkImportRunId.trim() } });
  };

  const bulkImportRun = data?.bulkImportRunByIdQuery;

  return (
    <div className={classes.container}>
      <h4>View Import Errors</h4>
      <div className={classes.inputContainer}>
        <div className={classes.inputRow}>
          <TextInput
            placeholder="Enter Bulk Import Run ID"
            value={bulkImportRunId}
            onChange={(value) => setBulkImportRunId(value)}
            className={classes.input}
          />
          <Button onClick={handleFetch} disabled={loading}>
            {loading ? 'Loading...' : 'Load Errors'}
          </Button>
        </div>

        {bulkImportRun && (
          <div className={classes.detailsContainer}>
            <div className={classes.detailsSection}>
              <div className={classes.detailsGrid}>
                <span className={classes.fieldLabel}>Import Run ID:</span>
                <span>{bulkImportRun._id}</span>
                <span className={classes.fieldLabel}>Import Type:</span>
                <span>{bulkImportRun.importType}</span>
                <span className={classes.fieldLabel}>Site Name:</span>
                <span>{bulkImportRun.siteName}</span>
                <span className={classes.fieldLabel}>Status:</span>
                <span>{bulkImportRun.importStatus}</span>
                <span className={classes.fieldLabel}>User:</span>
                <span>{bulkImportRun.userName || 'N/A'}</span>
                <span className={classes.fieldLabel}>Imported At:</span>
                <span>{bulkImportRun.whenLoaded ? new Date(bulkImportRun.whenLoaded).toLocaleString() : 'N/A'}</span>
                <span className={classes.fieldLabel}>Total Changes:</span>
                <span>{bulkImportRun.totalRows}</span>
                <span className={classes.fieldLabel}>Line Errors:</span>
                <span className={bulkImportRun.totalErrors > 0 ? classes.errorText : ''}>
                  {bulkImportRun.totalErrors}
                </span>
              </div>
            </div>

            {bulkImportRun.fileErrors && bulkImportRun.fileErrors.length > 0 && (
              <div className={classes.detailsSection}>
                <h5 className={classes.errorSectionTitle}>File Errors</h5>
                <div className={classes.fileErrorsContent}>
                  {bulkImportRun.fileErrors.map((fileError, idx) => (
                    <div
                      key={idx}
                      className={
                        idx < bulkImportRun.fileErrors.length - 1 ? classes.fileErrorWithBorder : classes.fileError
                      }
                    >
                      <div className={classes.errorDetail}>
                        <span className={classes.fieldLabel}>Code:</span> {fileError.code || 'N/A'}
                      </div>
                      <div className={classes.errorDetail}>
                        <span className={classes.fieldLabel}>Error:</span> {fileError.error}
                      </div>
                      {fileError.field && (
                        <div className={classes.errorDetail}>
                          <span className={classes.fieldLabel}>Field:</span> {fileError.field}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bulkImportRun.errors && bulkImportRun.errors.length > 0 && (
              <div className={classes.detailsSection}>
                <h5 className={classes.sectionTitle}>Row Errors ({bulkImportRun.errors.length})</h5>
                <div className={classes.rowErrorsContainer}>
                  {bulkImportRun.errors.map((rowError, idx) => {
                    return (
                      <div key={idx} className={classes.rowError}>
                        <div className={classes.lineNumber}>
                          <strong>Line {rowError.line}:</strong>
                        </div>
                        {rowError.data && <div className={classes.rowData}>{rowError.data}</div>}
                        {rowError.errorList && rowError.errorList.length > 0 && (
                          <div className={classes.errorListContainer}>
                            {rowError.errorList.map((error, errorIdx) => (
                              <div key={errorIdx} className={classes.errorItem}>
                                <span className={classes.errorBullet}>• </span>
                                {error.code && <strong>[{error.code}]</strong>} {error.error}
                                {error.field && <span className={classes.errorField}> (Field: {error.field})</span>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ViewImportErrorsWidget.propTypes = {
  classes: PropTypes.object,
};

export default ViewImportErrorsWidget;
